import { Modal } from 'bootstrap'
import { format } from 'date-fns'
import { i18n } from '@/libraries/i18n'
import { showToast } from './toast'

function createModal(bodyContent: string): HTMLElement {
  const modalHtml = `
    <div id="js-error-modal" class="modal" tabindex="-1" role="dialog" aria-modal="true" aria-hidden="true">
      <div class="modal-dialog modal-xl" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title">${i18n.t('error_pages.oops')}</h4>
            <button type="button" class="btn-close" aria-label="${i18n.t('common.actions.close')}" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">${bodyContent}</div>
        </div>
      </div>
    </div>
  `

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = modalHtml.trim()
  const modal = tempDiv.firstElementChild as HTMLElement
  document.body.appendChild(modal)

  return modal
}

async function createErrorModal(response: Response) {
  const clonedResponse = response.clone()
  const contentType = response.headers.get('content-type') || ''
  const text = await clonedResponse.text()

  let content: string

  if (contentType.includes('text/plain')) {
    // Wrap in <pre> tag to preserve formatting
    content = `<pre class="text-pre-wrap">${escapeHtml(text)}</pre>`
  } else {
    // Parse and scope error page styles to prevent them from affecting the main page
    const doc = new DOMParser().parseFromString(text, 'text/html')

    const styles = Array.from(doc.querySelectorAll('style, link[rel="stylesheet"]'))
    const scopedStylesHtml = styles
      .map((style) => {
        if (style.tagName === 'STYLE') {
          return `<style>${scopeCSS(style.textContent || '', '#js-error-modal')}</style>`
        }
        return ''
      })
      .join('\n')

    const bodyContent = doc.body
    content = scopedStylesHtml + (bodyContent?.innerHTML || '')
  }

  const existingModal = document.getElementById('js-error-modal')
  if (existingModal) {
    const modalBody = existingModal.querySelector('.modal-body')
    if (modalBody) {
      modalBody.innerHTML = content
    }
  } else {
    createModal(content)
  }
}

async function showErrorToast(response: Response): Promise<void> {
  const showDebugButton = import.meta.env['VITE_RUBY_MODE'] === 'development'

  let showDebugModalButton = `<div id="error-debug-button" class="mt-2 pt-2 border-top">
          <button type="button" class="btn bg-super-user text-white btn-sm">${i18n.t('common.visibility.show_details')}</button>
      </div>`

  const toast = showToast({
    body: i18n.t('error_pages.toast.body'),
    header: i18n.t('error_pages.toast.header'),
    type: 'error',
    autohide: false,
  })
  toast.id = 'js-error-toast'
  const timestamp = format(new Date(), i18n.t('datetime.formats.frontend.datefns.default'))
  toast.querySelector('.toast-header strong')?.insertAdjacentHTML('afterend', `<small>${timestamp}</small>`)

  if (showDebugButton) {
    toast.querySelector('.toast-body')?.insertAdjacentHTML('beforeend', showDebugModalButton)
    const showDetailsButton = document.querySelector('#error-debug-button')
    await createErrorModal(response)
    showDetailsButton?.addEventListener('click', () => {
      Modal.getOrCreateInstance('#js-error-modal').show()
    })
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

function scopeCSS(css: string, scopeSelector: string): string {
  return css.replace(/([^\r\n,{}]+)(,(?=[^}]*{)|\s*{)/g, (match, selector, separator) => {
    if (selector.trim().startsWith('@')) {
      return match
    }
    return `${scopeSelector} ${selector.trim()}${separator}`
  })
}

export async function handleErrors(response: Response): Promise<void> {
  if (!response.ok) {
    if (response.status === 500) {
      await showErrorToast(response)
    }
  }
}
